import * as Yup from 'yup';
import 'yup-phone-lite';

const req = () => Yup.string().trim().required('Required');
const phone = () => Yup.string().phone('US', 'Enter a valid phone number').required('Required');

const staffBaseSchema = Yup.object().shape({
  name: req(),
  email: Yup.string().when('sameAsCompanyEmail', {
    is: true,
    then: (s) => s.optional(),
    otherwise: (s) => s.email('Enter a valid email').required('Required'),
  }),
  sameAsCompanyEmail: Yup.boolean(),
});

const ownerSchema = staffBaseSchema.shape({
  ownershipPercentage: Yup.number()
    .typeError('Must be a number')
    .min(1, 'Min 1%')
    .max(100, 'Max 100%')
    .required('Required'),
  phone: phone(),
});

const staffWithTitleSchema = staffBaseSchema.shape({
  title: req(),
});

export const AffiliationFormSchema = Yup.object().shape({
  legalBusinessName: req(),
  dbaName: Yup.string().trim().optional(),
  ein: req(),
  timeInBusinessYears: Yup.number().typeError('Must be a number').min(0).required('Required'),
  timeInBusinessMonths: Yup.number().typeError('Must be a number').min(0).max(11, 'Max 11').required('Required'),
  businessPhone: phone(),
  businessFax: Yup.string().phone('US', 'Enter a valid fax number').optional(),
  businessWebsite: Yup.string().url('Enter a valid URL').optional(),
  companyEmail: Yup.string().email('Enter a valid email').required('Required'),

  businessRef1: req(),
  businessRef1Phone: phone(),
  businessRef2: req(),
  businessRef2Phone: phone(),

  physicalAddress: req(),
  physicalCity: req(),
  physicalState: req(),
  physicalZip: Yup.string().matches(/^\d{5}$/, 'Must be 5 digits').required('Required'),
  inventoryStored: Yup.string().oneOf(['yes', 'no']).required('Required'),

  inventoryAddress: Yup.string().when('inventoryStored', {
    is: 'no',
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),
  inventoryCity: Yup.string().when('inventoryStored', {
    is: 'no',
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),
  inventoryState: Yup.string().when('inventoryStored', {
    is: 'no',
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),
  inventoryZip: Yup.string().when('inventoryStored', {
    is: 'no',
    then: (s) => s.matches(/^\d{5}$/, 'Must be 5 digits').required('Required'),
    otherwise: (s) => s.optional(),
  }),

  mailingSameAsPhysical: Yup.boolean(),
  mailingAddress: Yup.string().when('mailingSameAsPhysical', {
    is: false,
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),
  mailingCity: Yup.string().when('mailingSameAsPhysical', {
    is: false,
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),
  mailingState: Yup.string().when('mailingSameAsPhysical', {
    is: false,
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),
  mailingZip: Yup.string().when('mailingSameAsPhysical', {
    is: false,
    then: (s) => s.matches(/^\d{5}$/, 'Must be 5 digits').required('Required'),
    otherwise: (s) => s.optional(),
  }),

  additionalSalesLocations: Yup.string().oneOf(['yes', 'no']).required('Required'),
  additionalLocationsSameAccount: Yup.string().when('additionalSalesLocations', {
    is: 'yes',
    then: (s) => s.oneOf(['yes', 'no']).required('Required'),
    otherwise: (s) => s.optional(),
  }),
  additionalLocation1Address: Yup.string().when('additionalSalesLocations', {
    is: 'yes',
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),
  additionalLocation1City: Yup.string().when('additionalSalesLocations', {
    is: 'yes',
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),
  additionalLocation1State: Yup.string().when('additionalSalesLocations', {
    is: 'yes',
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),
  additionalLocation1Zip: Yup.string().when('additionalSalesLocations', {
    is: 'yes',
    then: (s) => s.matches(/^\d{5}$/, 'Must be 5 digits').required('Required'),
    otherwise: (s) => s.optional(),
  }),
  // Location 2 is always optional
  additionalLocation2Address: Yup.string().optional(),
  additionalLocation2City: Yup.string().optional(),
  additionalLocation2State: Yup.string().optional(),
  additionalLocation2Zip: Yup.string().optional(),

  businessBankruptcy: Yup.string().oneOf(['yes', 'no']).required('Required'),
  businessBankruptcyDetails: Yup.string().when('businessBankruptcy', {
    is: 'yes',
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),
  ownerBankruptcy: Yup.string().oneOf(['yes', 'no']).required('Required'),
  ownerFelony: Yup.string().oneOf(['yes', 'no']).required('Required'),
  ownerFelonyDetails: Yup.string().when('ownerFelony', {
    is: 'yes',
    then: (s) => s.trim().required('Required'),
    otherwise: (s) => s.optional(),
  }),

  owners: Yup.array().of(ownerSchema).min(1, 'At least one owner is required'),
  management: Yup.array().of(staffWithTitleSchema),
  officeTitling: Yup.array().of(staffWithTitleSchema),
  sales: Yup.array().of(staffWithTitleSchema),
});

export const affiliationFormInitialValues = {
  legalBusinessName: '',
  dbaName: '',
  ein: '',
  timeInBusinessYears: '',
  timeInBusinessMonths: '',
  businessPhone: '',
  businessFax: '',
  businessWebsite: '',
  companyEmail: '',

  businessRef1: '',
  businessRef1Phone: '',
  businessRef2: '',
  businessRef2Phone: '',

  physicalAddress: '',
  physicalCity: '',
  physicalState: '',
  physicalZip: '',
  inventoryStored: '',
  inventoryAddress: '',
  inventoryCity: '',
  inventoryState: '',
  inventoryZip: '',

  mailingSameAsPhysical: false,
  mailingAddress: '',
  mailingCity: '',
  mailingState: '',
  mailingZip: '',

  additionalSalesLocations: '',
  additionalLocationsSameAccount: '',
  additionalLocation1Address: '',
  additionalLocation1City: '',
  additionalLocation1State: '',
  additionalLocation1Zip: '',
  additionalLocation2Address: '',
  additionalLocation2City: '',
  additionalLocation2State: '',
  additionalLocation2Zip: '',

  businessBankruptcy: '',
  businessBankruptcyDetails: '',
  ownerBankruptcy: '',
  ownerFelony: '',
  ownerFelonyDetails: '',

  owners: [{ name: '', ownershipPercentage: '', email: '', sameAsCompanyEmail: false, phone: '' }],
  management: [] as { name: string; title: string; email: string; sameAsCompanyEmail: boolean }[],
  officeTitling: [] as { name: string; title: string; email: string; sameAsCompanyEmail: boolean }[],
  sales: [] as { name: string; title: string; email: string; sameAsCompanyEmail: boolean }[],
};
